import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import useHandleAppException from '../../../../../hooks/useHandleAppException';
import LangEnum from '../../../../../lang/LangEnum';
import { useGetOrderById } from '../../../../../services/modules/order/order.queries';
import usePaymentReleaseSettings from '../../../../../stores/GeneralSettings/hooks/usePaymentReleaseSettings';
import snackbarStore from '../../../../../stores/Snackbar.store';
import { useTranslation } from '../../../../translation/TranslationContext';
import { ordersRepository } from '../../../repositories/ordersRepository/orders.repository';

interface UseOrderRefundPaymentModalProps {
  orderId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const useOrderRefundPaymentModal = ({
  onClose,
  orderId,
  onSuccess,
}: UseOrderRefundPaymentModalProps) => {
  const { translate } = useTranslation();
  const { handleErrorWithFallback } = useHandleAppException();
  const queryClient = useQueryClient();

  const { refundLimitHours } = usePaymentReleaseSettings();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { queryKey: orderQueryKey } = useGetOrderById({ enabled: false, id: orderId });

  const handleRefundPayment = async () => {
    setIsSubmitting(true);
    const response = await ordersRepository(orderId).refundOrderPaymentLink();
    setIsSubmitting(false);

    if (response.ok) {
      await queryClient.refetchQueries({
        queryKey: orderQueryKey,
        type: 'active',
        exact: true,
      });
      snackbarStore.handleMessage({
        message: translate(LangEnum.GLOBAL_SUCCESS_MESSAGE),
        type: 'success',
      });
      onSuccess?.();
      onClose();
      return;
    }

    snackbarStore.handleMessage({
      message: handleErrorWithFallback(
        response.data,
        translate(LangEnum.SNACKBAR_ERROR_OCCURRED_TRY_AGAIN),
      ),
      type: 'error',
    });
  };

  return {
    refundLimitHours,
    isSubmitting,
    handleRefundPayment,
  };
};

export default useOrderRefundPaymentModal;
